import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, RestaurantSummary, CreateRestaurantPayload } from '../api/client';

function CreateRestaurantModal({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<CreateRestaurantPayload>({
    restaurantId: '',
    name: '',
    address: '',
    phone: '',
    taxRate: 0.08,
    staffUsername: '',
    staffPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const slugify = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleNameChange = (name: string) => {
    setForm(f => ({ ...f, name, restaurantId: slugify(name) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.restaurantId || !form.name || !form.staffUsername || !form.staffPassword) {
      return setError('All required fields must be filled.');
    }
    if (form.staffPassword.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    setLoading(true);
    try {
      await adminApi.createRestaurant(form);
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create restaurant.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-sm';
  const labelCls = 'block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onCancel}>
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold text-lg mb-1">Add New Restaurant</h3>
        <p className="text-gray-400 text-sm mb-5">Create a restaurant and its staff login.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Restaurant Name *</label>
            <input value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="Capitol Cafe" required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Restaurant ID</label>
            <input value={form.restaurantId} onChange={e => setForm(f => ({ ...f, restaurantId: e.target.value }))} placeholder="capitol-cafe" required className={inputCls} />
            <p className="text-gray-600 text-xs mt-1">Auto-generated from name. Must be unique.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Address</label>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555-1234" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Tax Rate</label>
            <input type="number" step="0.01" min="0" max="0.5" value={form.taxRate} onChange={e => setForm(f => ({ ...f, taxRate: parseFloat(e.target.value) || 0 }))} className={inputCls} />
            <p className="text-gray-600 text-xs mt-1">e.g. 0.08 = 8%</p>
          </div>

          <div className="border-t border-white/5 pt-4">
            <p className="text-gray-300 text-sm font-semibold mb-3">Staff Login Credentials</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Username *</label>
                <input value={form.staffUsername} onChange={e => setForm(f => ({ ...f, staffUsername: e.target.value }))} placeholder="capitol-kitchen" required className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Password *</label>
                <input type="password" value={form.staffPassword} onChange={e => setForm(f => ({ ...f, staffPassword: e.target.value }))} placeholder="Min 8 characters" required className={inputCls} />
              </div>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors disabled:opacity-60">
              {loading ? 'Creating...' : 'Create Restaurant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const AdminDashboard: React.FC = () => {
  const [restaurants, setRestaurants] = useState<RestaurantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchRestaurants = useCallback(async () => {
    try {
      setError('');
      const list = await adminApi.listRestaurants();
      setRestaurants(list);
    } catch (err) {
      console.error('Failed to load restaurants:', err);
      setError('Failed to load restaurants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRestaurants(); }, [fetchRestaurants]);

  const handleCreated = () => {
    setShowCreate(false);
    setLoading(true);
    fetchRestaurants();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-black text-white">Restaurant Management</h1>
          <p className="text-gray-400 text-sm mt-1">{restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''} on the platform</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-lg shadow-purple-600/30"
        >
          + Add Restaurant
        </button>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
        </div>
      ) : restaurants.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🏪</div>
          <h2 className="text-white font-bold text-lg mb-2">No restaurants yet</h2>
          <p className="text-gray-400 text-sm mb-6">Add your first restaurant client to get started.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="px-6 py-3 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors"
          >
            + Add Restaurant
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {restaurants.map(r => (
            <div key={r.restaurantId} className="bg-gray-900/60 border border-white/8 rounded-2xl p-5 hover:border-purple-500/30 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-600/20 flex items-center justify-center text-purple-300 font-black text-lg">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">{r.name}</h3>
                    <p className="text-gray-500 text-sm">{r.restaurantId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  {r.address && (
                    <div className="text-gray-400">
                      <span className="text-gray-600">Address:</span> {r.address}
                    </div>
                  )}
                  {r.phone && (
                    <div className="text-gray-400">
                      <span className="text-gray-600">Phone:</span> {r.phone}
                    </div>
                  )}
                  <div className="text-gray-500 text-xs">
                    Created {new Date(r.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateRestaurantModal onCreated={handleCreated} onCancel={() => setShowCreate(false)} />
      )}
    </div>
  );
};
